import { motion } from "framer-motion";
import { Wrench, BatteryCharging, KeyRound, CircleDot, Droplet, Fuel, ArrowUpRight } from "lucide-react";

const IMG_A =
  "https://images.unsplash.com/photo-1615906655593-ad0386982a0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxtZWNoYW5pYyUyMGZpeGluZyUyMGNhciUyMGNpbmVtYXRpYyUyMGRhcmt8ZW58MHx8fHwxNzg1MTE2NDE3fDA&ixlib=rb-4.1.0&q=85";
const IMG_B =
  "https://images.unsplash.com/photo-1610641018556-030e920d6999?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHx0b3clMjB0cnVjayUyMG5pZ2h0JTIwY2luZW1hdGljfGVufDB8fHx8MTc4NTExNjQxN3ww&ixlib=rb-4.1.0&q=85";

const services = [
  { n: "01", title: "Car Maintenance", icon: Wrench, desc: "Routine checks and repairs to keep your vehicle running at its best." },
  { n: "02", title: "Battery Installation", icon: BatteryCharging, desc: "Dead battery? We test, replace and install on the spot." },
  { n: "03", title: "Lockout Service", icon: KeyRound, desc: "Locked out? We get you back inside quickly and safely." },
  { n: "04", title: "Tire Changes", icon: CircleDot, desc: "Flat tire assistance and fast roadside swaps, anywhere." },
  { n: "05", title: "Oil Changes", icon: Droplet, desc: "Clean oil, smooth engine. Convenient service that comes to you." },
  { n: "06", title: "Gas Delivery", icon: Fuel, desc: "Ran out of fuel? We bring the gas straight to your location." },
];

const reveal = {
  hidden: { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#050505] px-6 py-28 md:px-12 md:py-40"
    >
      <div className="mx-auto max-w-[1400px]">
        {/* Header */}
        <div className="mb-20 grid gap-8 md:grid-cols-12 md:items-end">
          <div className="md:col-span-8">
            <p className="overline mb-5 flex items-center gap-3 text-primary">
              <span className="h-px w-10 bg-primary" /> What We Do
            </p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="font-display text-5xl leading-[0.95] text-white sm:text-6xl lg:text-7xl"
            >
              SERVICES BUILT FOR <span className="text-primary">THE ROAD</span>
            </motion.h2>
          </div>
          <p className="text-white/60 md:col-span-4 md:text-right">
            Six core services. One dependable crew. Whatever left you stranded,
            we have the fix.
          </p>
        </div>

        {/* Feature images */}
        <div className="mb-20 grid gap-4 md:grid-cols-2">
          {[IMG_A, IMG_B].map((src, i) => (
            <motion.div
              key={i}
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              whileInView={{ clipPath: "inset(0% 0 0 0)" }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
              className="relative aspect-[16/10] overflow-hidden border border-white/10"
            >
              <motion.img
                src={src}
                alt="Storm's Roadside service"
                initial={{ scale: 1.3 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
            </motion.div>
          ))}
        </div>

        {/* Service chapters */}
        <div className="border-t border-white/10">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.n}
                data-testid={`service-item-${s.n}`}
                custom={i}
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                className="group grid grid-cols-12 items-center gap-4 border-b border-white/10 py-8 transition-colors duration-300 hover:bg-white/[0.03] md:py-10"
              >
                <span className="col-span-3 font-display text-4xl text-stroke-red transition-all duration-300 group-hover:text-primary md:col-span-2 md:text-6xl">
                  {s.n}
                </span>
                <div className="col-span-2 flex md:col-span-1">
                  <Icon className="h-7 w-7 text-primary md:h-9 md:w-9" />
                </div>
                <h3 className="col-span-7 font-display text-2xl leading-none text-white transition-transform duration-300 group-hover:translate-x-2 md:col-span-5 md:text-4xl">
                  {s.title}
                </h3>
                <p className="col-span-12 mt-3 text-white/55 md:col-span-3 md:mt-0 md:text-right">
                  {s.desc}
                </p>
                <div className="hidden justify-end md:col-span-1 md:flex">
                  <ArrowUpRight className="h-6 w-6 text-white/30 transition-all duration-300 group-hover:rotate-45 group-hover:text-primary" />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
